import { useEffect, useState, useCallback } from "react";
import { arbSessionApi } from "@/lib/api-client";
import { toast } from "sonner";
import { KeyRound, Loader2, Trash2, ShieldCheck } from "lucide-react";

const STATE_CLS = {
  STORED: "text-amber-300 border-amber-500/40",
  AUTH_OK: "text-emerald-400 border-emerald-500/40",
  SESSION_EXPIRED: "text-rose-300 border-rose-500/40",
  BLOCKED: "text-rose-300 border-rose-500/40",
  UNREACHABLE: "text-white/40 border-white/20",
};

export const SessionVaultPanel = ({ onChanged }) => {
  const [sessions, setSessions] = useState([]);
  const [platform, setPlatform] = useState("upwork");
  const [cookies, setCookies] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    arbSessionApi.list().then((r) => setSessions(r.sessions || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const store = async () => {
    if (!cookies.trim()) { toast.error("Paste the session cookies first"); return; }
    setBusy("store");
    try {
      const r = await arbSessionApi.store(platform, cookies);
      toast.success(`SESSION VAULT — ${r.platform} identity filed (${r.cookie_count} cookies, Fernet-encrypted). Scanner armed.`);
      setCookies("");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Session filing failed");
    } finally { setBusy(null); }
  };

  const remove = async (p) => {
    setBusy(`del-${p}`);
    try {
      await arbSessionApi.remove(p);
      toast.info(`${p} session purged from the Vault.`);
      load();
      onChanged?.();
    } catch { toast.error("Purge failed"); } finally { setBusy(null); }
  };

  return (
    <section className="border border-amber-500/25 bg-amber-500/[0.03] px-4 py-3 mb-4" data-testid="session-vault-panel">
      <div className="flex items-center gap-2 mb-2">
        <KeyRound size={11} className="text-amber-400" />
        <span className="font-mono-uc text-[10px] uppercase tracking-widest text-amber-400">
          Session Vault · encrypted platform identity
        </span>
        <span className="ml-auto font-mono-uc text-[8px] uppercase tracking-widest text-white/35 flex items-center gap-1">
          <ShieldCheck size={9} className="text-emerald-400" /> Fernet-encrypted · never displayed again · never logged
        </span>
      </div>
      <div className="flex items-start gap-2 flex-wrap">
        <select data-testid="session-platform-select" value={platform} onChange={(e) => setPlatform(e.target.value)}
                className="bg-black border border-white/15 px-2 py-1.5 text-[11px] text-white/90 focus:border-amber-500/50 outline-none">
          <option value="upwork">Upwork</option>
          <option value="fiverr">Fiverr</option>
          <option value="godaddy">GoDaddy (Venture Recovery)</option>
        </select>
        <textarea data-testid="session-cookies-input" value={cookies} onChange={(e) => setCookies(e.target.value)} rows={2}
                  placeholder="Paste session cookies — 'name=value; name2=value2' or a JSON cookie array (from your browser's DevTools)…"
                  className="flex-1 min-w-[240px] bg-black border border-white/15 px-2 py-1.5 text-[11px] text-white/90 focus:border-amber-500/50 outline-none resize-none font-mono-uc" />
        <button data-testid="session-store-btn" onClick={store} disabled={!!busy}
                className="uc-btn-amber px-3 py-1.5 font-mono-uc text-[9px] uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50">
          {busy === "store" ? <Loader2 size={10} className="animate-spin" /> : <KeyRound size={10} />} File Session (Encrypted)
        </button>
      </div>
      {sessions.length > 0 && (
        <div className="mt-2.5 space-y-1" data-testid="session-list">
          {sessions.map((s) => (
            <div key={s.platform} className="flex items-center gap-2 border border-white/10 bg-black/40 px-2 py-1.5"
                 data-testid={`session-row-${s.platform}`}>
              <span className="font-mono-uc text-[10px] uppercase tracking-widest text-white/80">{s.platform}</span>
              <span className="font-mono-uc text-[8px] text-white/40">{s.cookie_count} cookie(s)</span>
              <span data-testid={`session-status-${s.platform}`}
                    className={`font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-0.5 border ${STATE_CLS[s.last_status] || STATE_CLS.UNREACHABLE}`}>
                {s.last_status || "STORED"}
              </span>
              {s.last_scan_at && <span className="font-mono-uc text-[8px] text-white/30">last scan {s.last_scan_at.slice(0, 16)}Z</span>}
              <button data-testid={`session-delete-${s.platform}`} onClick={() => remove(s.platform)} disabled={!!busy}
                      className="ml-auto text-white/30 hover:text-rose-300 transition-colors disabled:opacity-50">
                {busy === `del-${s.platform}` ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
              </button>
            </div>
          ))}
        </div>
      )}
      {sessions.length === 0 && (
        <div className="font-mono-uc text-[9px] text-white/40 mt-2" data-testid="session-empty-note">
          SCANNER DORMANT — no identity filed. Only Vault-stored sessions are ever used; public/downloaded API keys are forbidden.
        </div>
      )}
    </section>
  );
};
